import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '../../environments/environment';
import { InstallAppDialogComponent } from '../pages/common-components/install-app-dialog/install-app-dialog.component';
import { MessagingService } from './messaging.service';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

@Injectable({
  providedIn: 'root',
})
export class InstallPromptService {
  private deferredInstallPrompt: BeforeInstallPromptEvent | null = null;
  private installDialogOpen = false;
  private readonly installDismissKey = 'installPromptDismissedAt';
  private readonly installDismissForMs = 7 * 24 * 60 * 60 * 1000;
  private readonly giftVoucherHandoffKey = 'giftVoucherPwaHandoff';

  constructor(
    private dialog: MatDialog,
    private messaging: MessagingService,
  ) {
    window.addEventListener('beforeinstallprompt', this.onBeforeInstallPrompt);
    window.addEventListener('appinstalled', this.onAppInstalled);
  }

  /** True when the site is already running as an installed Home Screen app. */
  isAppInstalled(): boolean {
    return this.messaging.isStandalonePwa() || localStorage.getItem('pwaInstalled') === 'true';
  }

  /** Phones and tablets only — never show the install prompt on laptop/desktop. */
  isMobileDevice(): boolean {
    const ua = window.navigator.userAgent || '';
    if (/Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)) {
      return true;
    }
    // iPadOS 13+ reports as MacIntel with touch points.
    if (/iPad/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
      return true;
    }
    return false;
  }

  /** Profile "Install App" row — mobile and not already installed. */
  canShowInstallEntry(): boolean {
    return this.isMobileDevice() && !this.isAppInstalled();
  }

  /**
   * Gift-voucher links are usually opened from WhatsApp / Instagram / Facebook.
   * Those in-app browsers ignore the installed WebAPK. On Android, hand the HTTPS
   * URL to the system so the installed PWA can capture it (do not force Chrome).
   * Also re-routes a normal Chrome tab when we already know the app was installed.
   * Returns true when a handoff navigation was started.
   */
  tryHandoffGiftVoucherToInstalledApp(): boolean {
    if (this.messaging.isStandalonePwa() || this.messaging.isIosDevice()) {
      return false;
    }
    if (!this.isAndroid() || !this.hasGiftVoucherParams()) {
      return false;
    }
    if (sessionStorage.getItem(this.giftVoucherHandoffKey) === '1') {
      return false;
    }

    const shouldHandoff =
      this.isInAppBrowser() || localStorage.getItem('pwaInstalled') === 'true';
    if (!shouldHandoff) {
      return false;
    }

    sessionStorage.setItem(this.giftVoucherHandoffKey, '1');
    this.openViaAndroidIntent(window.location.href);
    return true;
  }

  /** Automatic prompt after the app loads. Skipped when the user already dismissed it. */
  tryAutoShow(): void {
    if (!this.isMobileDevice()) {
      return;
    }
    if (window.location.pathname.includes('payment-success')) {
      return;
    }
    if (this.isAppInstalled() || this.installPromptDismissedRecently()) {
      return;
    }
    // Gift-voucher deep links should prefer opening the installed app, not the install card.
    if (this.hasGiftVoucherParams() && this.isInAppBrowser()) {
      return;
    }
    if (!this.messaging.isIosDevice() && !this.deferredInstallPrompt) {
      return;
    }
    this.openDialog(false);
  }

  /** Profile menu. Opens even if the automatic prompt was dismissed. */
  openFromProfile(): void {
    if (!this.isMobileDevice() || this.isAppInstalled()) {
      return;
    }
    this.openDialog(true);
  }

  /**
   * Leave an Android in-app browser via a VIEW intent.
   * Do not pin package=com.android.chrome — that always opens a browser tab
   * and bypasses an already-installed WebAPK / PWA.
   */
  openOutsideInAppBrowser(url: string = window.location.href): void {
    if (!this.isAndroid()) {
      return;
    }
    this.openViaAndroidIntent(url);
  }

  private readonly onBeforeInstallPrompt = (event: Event) => {
    event.preventDefault();
    this.deferredInstallPrompt = event as BeforeInstallPromptEvent;
    this.tryAutoShow();
  };

  private readonly onAppInstalled = () => {
    this.deferredInstallPrompt = null;
    localStorage.setItem('pwaInstalled', 'true');
  };

  private openDialog(fromProfile: boolean): void {
    if (!this.isMobileDevice() || this.installDialogOpen) {
      return;
    }

    this.installDialogOpen = true;
    const dialogRef = this.dialog.open(InstallAppDialogComponent, {
      panelClass: 'install-app-dialog-panel',
      backdropClass: 'install-app-dialog-backdrop',
      width: '340px',
      maxWidth: 'calc(100vw - 32px)',
      autoFocus: false,
      data: {
        appName: localStorage.getItem('name') || environment.companyName || 'Passbook',
        logo: localStorage.getItem('downloadLogo') || environment.logoImage || '',
        themeColor: localStorage.getItem('primaryColor') || environment.primaryColor || '#EB1F27',
        isIos: this.messaging.isIosDevice(),
        tryNativeInstall: () => this.promptNativeInstall(),
        openOutsideInAppBrowser: () => this.openOutsideInAppBrowser(),
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      this.installDialogOpen = false;
      if (result === 'installed') {
        localStorage.setItem('pwaInstalled', 'true');
        return;
      }
      if (!fromProfile) {
        this.rememberInstallDismiss();
      }
    });
  }

  private hasGiftVoucherParams(): boolean {
    const params = new URLSearchParams(window.location.search);
    const voucherCode = (params.get('voucherCode') || '').trim();
    const campaignId = (params.get('campaignId') || '').trim();
    return !!(voucherCode && campaignId);
  }

  private isAndroid(): boolean {
    return /Android/i.test(window.navigator.userAgent || '');
  }

  private isInAppBrowser(): boolean {
    return /FBAN|FBAV|Instagram|WhatsApp|Line\/|TikTok|Musical|Snapchat|; wv\)/i.test(
      window.navigator.userAgent || ''
    );
  }

  private openViaAndroidIntent(url: string): void {
    const stripped = url.replace(/^https?:\/\//, '');
    window.location.href =
      `intent://${stripped}#Intent;scheme=https;` +
      `action=android.intent.action.VIEW;` +
      `category=android.intent.category.BROWSABLE;` +
      `S.browser_fallback_url=${encodeURIComponent(url)};end`;
  }

  private installPromptDismissedRecently(): boolean {
    const raw = localStorage.getItem(this.installDismissKey);
    if (!raw) {
      return false;
    }
    const dismissedAt = Number(raw);
    return Number.isFinite(dismissedAt) && Date.now() - dismissedAt < this.installDismissForMs;
  }

  private rememberInstallDismiss(): void {
    localStorage.setItem(this.installDismissKey, String(Date.now()));
  }

  private promptNativeInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
    const promptEvent = this.deferredInstallPrompt;
    if (!promptEvent) {
      return Promise.resolve('unavailable');
    }

    const promptResult = promptEvent.prompt();
    this.deferredInstallPrompt = null;
    return promptResult.then(() => promptEvent.userChoice).then((choice) =>
      choice?.outcome === 'accepted' ? 'accepted' : 'dismissed'
    );
  }
}
